/**
 * SaveToPlaybookModal
 *
 * Two-step modal triggered from ThreadTabBar:
 *   1. "Drafting…" — calls POST .../draft-playbook-goal; shows skeleton loader
 *   2. "Review & save" — user edits goal/name and reviews the output. A Word
 *      report is always produced; an Excel template can optionally be attached
 *      as an additional output the agent also fills.
 *
 * On success we show a toast (no redirect). When a template is attached we
 * create the agent, then upload the template.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import SlideshowOutlined from '@mui/icons-material/SlideshowOutlined';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import EventRepeatRounded from '@mui/icons-material/EventRepeatRounded';
import AppDialog from './dialog/AppDialog';
import { draftPlaybookGoal, createPlaybook, uploadPlaybookTemplate, deletePlaybook, validatePlaybookTemplate } from '../services/playbooksService';
import type { DetectedPrimaryPeriod, TemplateValidationResult } from '../types/playbooks';
import { SnackbarContext } from '../ui/SnackbarProvider';

type PeriodMode = 'rolling' | 'fixed' | 'ask';

interface Props {
  open: boolean;
  onClose: () => void;
  threadId: string | null;
}

type Step = 'drafting' | 'review' | 'saving';

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SaveToPlaybookModal({ open, onClose, threadId }: Props) {
  const { notify } = React.useContext(SnackbarContext);

  const [step, setStep] = useState<Step>('drafting');
  const [draftError, setDraftError] = useState<string | null>(null);

  // Form state
  const [goal, setGoal] = useState('');
  const [name, setName] = useState('');
  // Output: a Word document is always produced; an Excel template is an
  // optional additional output the agent also fills.
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [templateChecking, setTemplateChecking] = useState(false);
  const [templateValidation, setTemplateValidation] = useState<TemplateValidationResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [visibility, setVisibility] = useState<'org' | 'private'>('org');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Reporting period (detected from chat) + the user's explicit choice.
  const [primaryPeriod, setPrimaryPeriod] = useState<DetectedPrimaryPeriod>({ absolute: '', relative: '' });
  const [otherWindows, setOtherWindows] = useState<string[]>([]);
  const [periodMode, setPeriodMode] = useState<PeriodMode>('ask');

  // Reset and draft goal whenever modal opens
  useEffect(() => {
    if (!open || !threadId) return;
    setStep('drafting');
    setDraftError(null);
    setGoal('');
    setName('');
    setTemplateFile(null);
    setTemplateChecking(false);
    setTemplateValidation(null);
    setDragOver(false);
    setSubmitError(null);
    setPrimaryPeriod({ absolute: '', relative: '' });
    setOtherWindows([]);
    setPeriodMode('ask');

    draftPlaybookGoal(threadId)
      .then((res) => {
        setGoal(res.goal);
        // Prefer the LLM-generated name; fall back to a short version of the
        // goal (first 60 chars, trimmed at a word boundary) if it's missing.
        const fallback = res.goal.length > 60
          ? res.goal.slice(0, 57).replace(/\s\S*$/, '') + '…'
          : res.goal;
        setName((res.name || '').trim() || fallback);

        const pp = res.primary_period || { absolute: '', relative: '' };
        setPrimaryPeriod(pp);
        setOtherWindows(res.other_windows || []);
        // Default the picker: a confident single primary defaults to Rolling;
        // anything ambiguous defaults to "decide at run time".
        const confident = res.anchor_confidence === 'high';
        setPeriodMode(confident && pp.relative ? 'rolling' : 'ask');

        setStep('review');
      })
      .catch((err: Error) => {
        setDraftError(err.message || 'Failed to draft goal');
        setStep('review');
      });
  }, [open, threadId]);

  const pickTemplate = () => fileRef.current?.click();

  const acceptFile = async (file: File | null | undefined) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setSubmitError('Template must be a .xlsx file');
      return;
    }
    setSubmitError(null);
    setTemplateFile(file);
    setTemplateValidation(null);
    setTemplateChecking(true);
    try {
      const result = await validatePlaybookTemplate(file);
      setTemplateValidation(result);
    } catch (err: any) {
      // Couldn't reach the validator — surface a single generic problem so the
      // Save button stays disabled rather than letting an unchecked file through.
      setTemplateValidation({
        ok: false,
        errors: [{ error: err?.message || 'Could not validate the template. Try again.' }],
        manifest: null,
      });
    } finally {
      setTemplateChecking(false);
    }
  };

  const clearTemplate = () => {
    setTemplateFile(null);
    setTemplateValidation(null);
    setTemplateChecking(false);
  };

  const onTemplateChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    acceptFile(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (isBusy) return;
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const handleSave = async () => {
    if (!threadId) return;
    if (!goal.trim()) {
      setSubmitError('Please describe the goal of this agent');
      return;
    }
    if (!name.trim()) {
      setSubmitError('Please give this agent a name');
      return;
    }
    // The Word document is always produced. An Excel template is an optional
    // ADD-ON; only validate it when one has been attached.
    if (templateFile) {
      if (templateChecking) {
        setSubmitError('Still checking the template — one moment.');
        return;
      }
      if (!templateValidation?.ok) {
        setSubmitError('Fix the template problems listed above before saving.');
        return;
      }
    }
    setStep('saving');
    setSubmitError(null);
    try {
      // Translate the period choice into the strategy string the agent reads.
      let periodStrategy = '';
      if (periodMode === 'rolling') periodStrategy = primaryPeriod.relative || '';
      else if (periodMode === 'fixed' && primaryPeriod.absolute) periodStrategy = `Fixed window: ${primaryPeriod.absolute}`;
      // 'ask' → leave empty so the agent confirms on its first run.
      // The picker is only shown when a window was detected; when shown, the
      // choice (incl. an empty "decide at run time") is deliberate and must
      // not be overwritten by the brief LLM's inference.
      const periodChoiceExplicit = !!(primaryPeriod.absolute || primaryPeriod.relative);

      // The agent always authors a Word document. An attached Excel template is
      // an additional output the agent also fills.
      const created = await createPlaybook({
        source_thread_id: threadId,
        name: name.trim(),
        goal: goal.trim(),
        output_format: 'docx',
        visibility,
        period_strategy: periodStrategy,
        period_choice_explicit: periodChoiceExplicit,
      });

      if (templateFile) {
        try {
          // The template was already validated on selection, so this should
          // succeed. If it somehow fails (e.g. a transient error), roll back the
          // just-created agent so we never leave a half-created agent behind.
          await uploadPlaybookTemplate(created.playbook_id, templateFile);
        } catch (upErr: any) {
          try {
            await deletePlaybook(created.playbook_id);
          } catch {
            /* best-effort cleanup; the inline error is what matters */
          }
          setSubmitError(upErr?.message || 'The template could not be attached. Try again.');
          setStep('review');
          return;
        }
        notify(`Agent “${name.trim()}” saved — it'll write a report and fill your Excel template each run.`, 'success');
        onClose();
        return;
      }

      notify(`Agent “${name.trim()}” saved. Find it under Agents to run it anytime.`, 'success');
      onClose();
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to save agent');
      setStep('review');
    }
  };

  const isBusy = step === 'drafting' || step === 'saving';
  const isReview = step === 'review';

  const nameValid = name.trim().length > 0;
  const goalValid = goal.trim().length > 0;
  // Word is always valid; if a template add-on is attached it must check out.
  const outputValid =
    !templateFile || (!templateChecking && templateValidation?.ok === true);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Save as Agent"
      subtitle="Turn this analysis into a reusable agent that can be re-run against fresh data."
      icon={<SmartToyOutlined sx={{ color: 'primary.main' }} />}
      size="sm"
      busy={isBusy}
      primaryAction={isReview ? {
          label: 'Save Agent',
        onClick: handleSave,
        disabled: !nameValid || !goalValid || !outputValid,
      } : undefined}
      dividers
    >
      {step === 'drafting' && (
        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
          <CircularProgress size={32} />
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeRounded sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">
              AI is summarising the goal from your chat…
            </Typography>
          </Stack>
        </Stack>
      )}

      {(step === 'review' || step === 'saving') && (
        <Stack spacing={2.5}>
          {draftError && (
            <Alert severity="warning" sx={{ fontSize: '0.8125rem' }}>
              Could not auto-draft the goal ({draftError}). Please describe it manually below.
            </Alert>
          )}

          {!draftError && goal && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: -0.5 }}>
              <AutoAwesomeRounded sx={{ fontSize: 14, color: 'primary.main' }} />
              <Typography variant="caption" color="primary.main" fontWeight={600}>
                AI-drafted · edit to refine
              </Typography>
            </Box>
          )}

          {/* Name */}
          <TextField
            label="Agent name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Weekly Churn Risk Report"
            disabled={isBusy}
            required
            inputProps={{ maxLength: 120 }}
          />

          {/* Goal — the core of the agent */}
          <TextField
            label="Goal"
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="e.g. Generate a weekly churn risk report with top 20 at-risk customers and recommended actions"
            helperText="The agent will work towards this goal every time it runs."
            disabled={isBusy}
            required
          />

          {/* Reporting period — only when the chat referenced a window */}
          {(primaryPeriod.absolute || primaryPeriod.relative) && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, letterSpacing: 0.3 }}>
                <EventRepeatRounded sx={{ fontSize: 15 }} /> WHICH TIME PERIOD SHOULD IT ANALYZE?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                You can re-run this agent later on fresh data{primaryPeriod.absolute ? `. This chat looked at ${primaryPeriod.absolute}` : ''}. Choose what time period each future run should cover.
              </Typography>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, px: 1.5, py: 0.5 }}>
                <RadioGroup value={periodMode} onChange={(e) => setPeriodMode(e.target.value as PeriodMode)}>
                  {primaryPeriod.relative && (
                    <FormControlLabel
                      value="rolling"
                      disabled={isBusy}
                      control={<Radio size="small" />}
                      sx={{ alignItems: 'flex-start', py: 0.5, '& .MuiRadio-root': { pt: 0.25 } }}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Always the latest {primaryPeriod.relative} <Typography component="span" variant="caption" color="primary.main" fontWeight={600}>(Recommended)</Typography>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Each run automatically uses the most recent {primaryPeriod.relative} at that time{primaryPeriod.absolute ? `, so it moves forward instead of staying on ${primaryPeriod.absolute}` : ''}. Best for a recurring report.
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                  {primaryPeriod.absolute && (
                    <FormControlLabel
                      value="fixed"
                      disabled={isBusy}
                      control={<Radio size="small" />}
                      sx={{ alignItems: 'flex-start', py: 0.5, '& .MuiRadio-root': { pt: 0.25 } }}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>Always {primaryPeriod.absolute}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Every run analyzes this exact same period. Best for a one-off or locked report.
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                  <FormControlLabel
                    value="ask"
                    disabled={isBusy}
                    control={<Radio size="small" />}
                    sx={{ alignItems: 'flex-start', py: 0.5, '& .MuiRadio-root': { pt: 0.25 } }}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Ask me each time</Typography>
                        <Typography variant="caption" color="text.secondary">
                          The agent checks with you which period to use whenever it runs.
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>
              {otherWindows.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Comparisons stay relative to the chosen window: {otherWindows.join('; ')}.
                </Typography>
              )}
            </Box>
          )}

          {/* Output — how the agent produces its result */}
          <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onTemplateChosen} />
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1, letterSpacing: 0.3 }}>
              OUTPUT
            </Typography>
            {/* Primary output — always a Word document. */}
            <Box
              sx={(t) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.25,
                borderRadius: 1.5,
                border: `1px solid ${alpha(t.palette.primary.main, 0.35)}`,
                bgcolor: alpha(t.palette.primary.main, 0.05),
              })}
            >
              <DescriptionOutlined sx={{ color: 'primary.main', fontSize: 22, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>Word document</Typography>
                <Typography variant="caption" color="text.secondary">
                  A polished written report, authored from scratch every run.
                </Typography>
              </Box>
              <Chip label="Always on" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }} />
            </Box>

            {/* Optional additional outputs. */}
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mt: 2, mb: 1, letterSpacing: 0.3 }}>
              ADD ANOTHER OUTPUT (OPTIONAL)
            </Typography>

            {templateFile ? (
              // Selected state — file row + inline validation status.
              (() => {
                const invalid = !templateChecking && templateValidation?.ok === false;
                const valid = !templateChecking && templateValidation?.ok === true;
                const accent = invalid ? 'error' : 'success';
                const scalarCount = templateValidation?.manifest?.scalars?.length ?? 0;
                const tableCount = templateValidation?.manifest?.tables?.length ?? 0;
                return (
                  <Box sx={{ mt: 1.5 }}>
                    <Box
                      sx={(t) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.25,
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(t.palette[accent].main, 0.4)}`,
                        bgcolor: alpha(t.palette[accent].main, 0.06),
                      })}
                    >
                      <InsertDriveFileRounded sx={{ color: `${accent}.main`, fontSize: 22, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{templateFile.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {templateChecking
                            ? 'Checking template…'
                            : valid
                            ? `Looks good — ${scalarCount} value${scalarCount === 1 ? '' : 's'}${tableCount ? ` · ${tableCount} table${tableCount === 1 ? '' : 's'}` : ''} detected`
                            : invalid
                            ? `${formatBytes(templateFile.size)} · please fix the issues below`
                            : `${formatBytes(templateFile.size)} · the agent fills this every run`}
                        </Typography>
                      </Box>
                      {templateChecking && <CircularProgress size={16} sx={{ flexShrink: 0 }} />}
                      <Button size="small" onClick={pickTemplate} disabled={isBusy} sx={{ textTransform: 'none', flexShrink: 0 }}>
                        Change
                      </Button>
                      <IconButton size="small" onClick={clearTemplate} disabled={isBusy} aria-label="Remove template">
                        <CloseRounded sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>

                    {invalid && (
                      <Alert severity="error" sx={{ mt: 1, fontSize: '0.8125rem' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.8125rem' }}>
                          This template has {templateValidation!.errors.length} issue
                          {templateValidation!.errors.length === 1 ? '' : 's'} to fix:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                          {templateValidation!.errors.slice(0, 8).map((e, i) => (
                            <li key={i}>
                              {(e.sheet || e.cell) && (
                                <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                  {[e.sheet, e.cell].filter(Boolean).join('!')}:{' '}
                                </Box>
                              )}
                              {e.error}
                            </li>
                          ))}
                          {templateValidation!.errors.length > 8 && (
                            <li>…and {templateValidation!.errors.length - 8} more.</li>
                          )}
                        </Box>
                      </Alert>
                    )}
                  </Box>
                );
              })()
            ) : (
              // Empty state — full-width clickable drop zone.
              <Box
                role="button"
                tabIndex={0}
                onClick={pickTemplate}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pickTemplate(); } }}
                onDragOver={(e) => { e.preventDefault(); if (!isBusy) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                sx={(t) => ({
                  mt: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 2,
                  py: 2.5,
                  borderRadius: 1.5,
                  border: `1.5px dashed ${dragOver ? t.palette.primary.main : t.palette.divider}`,
                  bgcolor: dragOver ? alpha(t.palette.primary.main, 0.06) : 'transparent',
                  cursor: isBusy ? 'default' : 'pointer',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': { borderColor: isBusy ? t.palette.divider : t.palette.primary.main },
                })}
              >
                <UploadFileRounded sx={{ fontSize: 24, color: dragOver ? 'primary.main' : 'text.secondary' }} />
                <Typography variant="body2" fontWeight={600}>
                  Also fill an Excel template — upload, or drag an .xlsx here
                </Typography>
                <Typography variant="caption" color="text.secondary" align="center" sx={{ maxWidth: 380 }}>
                  Optional. Use placeholders like <Box component="code" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>{'{{ metric }}'}</Box>;
                  the agent fills them every run alongside the Word report, preserving your formatting, formulas, and layout.
                </Typography>
              </Box>
            )}

            {/* PowerPoint — signalled as a future option. */}
            <Box
              sx={(t) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.25,
                mt: 1.5,
                borderRadius: 1.5,
                border: `1px dashed ${t.palette.divider}`,
                opacity: 0.7,
              })}
            >
              <SlideshowOutlined sx={{ color: 'text.secondary', fontSize: 22, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">PowerPoint deck</Typography>
                <Typography variant="caption" color="text.secondary">
                  Auto-generated slide summary of each run.
                </Typography>
              </Box>
              <Chip label="Coming soon" size="small" sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }} />
            </Box>
          </Box>

          {/* Visibility */}
          <FormControl fullWidth size="small">
            <InputLabel>Visibility</InputLabel>
            <Select
              value={visibility}
              label="Visibility"
              onChange={(e) => setVisibility(e.target.value as 'org' | 'private')}
              disabled={isBusy}
            >
              <MenuItem value="org">Team (shared with workspace)</MenuItem>
              <MenuItem value="private">Personal (only me)</MenuItem>
            </Select>
          </FormControl>

          {submitError && (
            <Alert severity="error" sx={{ fontSize: '0.8125rem' }}>
              {submitError}
            </Alert>
          )}
        </Stack>
      )}
    </AppDialog>
  );
}
